// Mirrors the legacy /about-us page section for section.

import { Link } from 'react-router-dom';
import { IMG, COLORS, FONTS, HEADING, TYPE } from 'styles/theme';
import { StaticMarketingPage } from 'components/marketing/StaticMarketingPage';
import { useMediaQuery } from 'hooks/useMediaQuery';
import NewsletterSignup from 'components/marketing/NewsletterSignup';

const APPROACH = [
  [IMG.aboutApproachNutrition, 'Science-Backed Nutrition'],
  [IMG.aboutApproachMenus, 'Chef-Developed Menus'],
  [IMG.aboutApproachFood, 'Delicious, Nourishing Food!'],
];

const PHILOSOPHY = [
  ["How we're different", 'We don’t put you in a bucket or serve you standard meals, each dish has been crafted especially for you.'],
  ['Plant-forward', 'Our meals can be made vegetarian as, fruits and veggies always play a starring role in all our meals.'],
  ['Rich in lean proteins', "Each meal is planned by a dietitian to include enough protein to keep you satiated and meet your body's needs."],
  ['Easy on the starch', 'Appropriate portions of high-fiber whole grains and starchy vegetables.'],
  ['Clean food', 'Our meals may sound indulgent (props to our talented chefs!) but they are clean and well-balanced. Our food is high in vitamins and minerals and uses small amounts of salt and high quality healthy fats. With good, clean ingredients, our meals have that homemade feel.'],
  ['Different every day', 'Good nutrition relies on eating diverse foods.'],
  ['Not a diet fad', "We don't cut out carbs or bulk on proteins. Our meals are based on your needs and balance so you're not missing out on any goodies."],
];

const SUSTAINABILITY = [
  ['Less waste.', [
    'We use eco-friendly packaging that is recyclable and compostable.',
    'To minimize waste, we plan ahead, and only buy what’s needed.',
  ]],
  ['Sourced locally.', [
    'Nutrients degrade over time. Shipping damages the environment. Our ingredients are seasonal and sourced locally to ensure they are fresh and nutrient-dense.',
    'We believe in using whole foods and cook from scratch. We even make our own sauces and dressings!',
  ]],
];

const SectionHeading = ({ children, align = 'left' }) => (
  <div style={{ textAlign: align }}>
    <h2 style={{ ...HEADING, fontSize: TYPE.sectionHeading, lineHeight: 1.2, margin: '0 0 18px' }}>
      {children}
    </h2>
    <div style={{
      width: '100%', maxWidth: align === 'center' ? '520px' : 'none', height: '1px',
      background: 'rgba(51,52,59,0.25)', margin: align === 'center' ? '0 auto 28px' : '0 0 28px',
    }} />
  </div>
);

/** Absolutely positioned photo stack; falls back to a plain grid on narrow screens. */
const Collage = ({ items, ratio, isWide }) => {
  if (!isWide) {
    return (
      <div style={{ display: 'grid', gap: '14px' }}>
        {items.map(it => (
          <img key={it.src} src={it.src} alt={it.alt} style={{
            width: '100%', height: '260px', objectFit: 'cover', borderRadius: '4px', display: 'block',
          }} />
        ))}
      </div>
    );
  }
  return (
    <div style={{ position: 'relative', width: '100%', paddingTop: ratio }}>
      {items.map((it, i) => (
        <img key={it.src} src={it.src} alt={it.alt} style={{
          position: 'absolute', ...it.at, objectFit: 'cover', borderRadius: '4px',
          boxShadow: '0 18px 40px rgba(0,0,0,0.16)', zIndex: i + 1,
        }} />
      ))}
    </div>
  );
};

export default function AboutPage() {
  const isWide = useMediaQuery('(min-width: 900px)');
  const twoCol = (imageFirst) => ({
    display: 'grid',
    gridTemplateColumns: isWide ? (imageFirst ? '5fr 6fr' : '6fr 5fr') : '1fr',
    gap: isWide ? '56px' : '36px',
    alignItems: 'center',
  });
  const body = { fontSize: TYPE.body, lineHeight: 1.8, color: COLORS.text };

  return (
    <StaticMarketingPage banner={{ image: IMG.aboutBanner, title: 'About Us' }}>
      {/* The Mission */}
      <section style={{ background: COLORS.cream, padding: isWide ? '90px 28px' : '60px 24px' }}>
        <div style={{ maxWidth: '1140px', margin: '0 auto', ...twoCol(false) }}>
          <div>
            <SectionHeading>The Mission</SectionHeading>
            <p style={{ ...body, margin: 0 }}>
              Did you know that <strong>eating right is key to feeling your best physically and mentally?</strong>{' '}
              In fact, our clients report weight loss, feeling more alert, focused, and having more energy.{' '}
              <strong>Everyone’s needs are different and your plate should reflect that.</strong> That’s why{' '}
              <strong>our mission is to make it straightforward and sustainable to eat well.</strong> Our registered
              dietitians and chefs work to create <strong>delicious meals tailored to your needs</strong> so that you
              can feel your best and hit all your goals.
            </p>
          </div>
          <Collage
            isWide={isWide}
            ratio="92%"
            items={[
              { src: IMG.aboutMission, alt: 'A bowl of fresh salad', at: { top: 0, left: 0, width: '82%', height: '66%' } },
              { src: IMG.aboutDelivery, alt: 'Square Fare meals handed over at the door', at: { bottom: 0, right: 0, width: '46%', height: '62%' } },
            ]}
          />
        </div>
      </section>

      {/* Our Approach */}
      <section style={{ background: COLORS.creamAlt, padding: isWide ? '90px 28px' : '60px 24px' }}>
        <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
          <SectionHeading align="center">Our Approach</SectionHeading>
          <div style={{
            display: 'grid', gridTemplateColumns: isWide ? 'repeat(3, 1fr)' : '1fr', gap: isWide ? '26px' : '28px',
          }}>
            {APPROACH.map(([img, caption]) => (
              <div key={caption}>
                <img src={img} alt={caption} style={{
                  width: '100%', height: isWide ? '440px' : '320px', objectFit: 'cover',
                  borderRadius: '4px', display: 'block',
                }} />
                <p style={{
                  ...HEADING, fontSize: TYPE.cardTitle, textAlign: 'center', margin: '20px 0 0',
                  color: COLORS.dark,
                }}>{caption}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '44px' }}>
            <Link to="/quiz" style={{
              display: 'inline-block', padding: '15px 44px', background: COLORS.dark, color: '#fff',
              textDecoration: 'none', borderRadius: '4px', fontWeight: 700, fontSize: TYPE.button,
              letterSpacing: '1.5px', textTransform: 'uppercase',
            }}>Get Started</Link>
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section style={{ background: COLORS.cream, padding: isWide ? '90px 28px' : '60px 24px' }}>
        <div style={{ maxWidth: '1140px', margin: '0 auto', ...twoCol(true), alignItems: 'start' }}>
          <img src={IMG.aboutPhilosophy} alt="Fresh vegetables on a wooden board" style={{
            width: '100%', height: isWide ? '820px' : '340px', objectFit: 'cover',
            borderRadius: '4px', display: 'block', position: isWide ? 'sticky' : 'static', top: '90px',
          }} />
          <div>
            <SectionHeading>Our Philosophy</SectionHeading>
            {PHILOSOPHY.map(([title, text]) => (
              <div key={title} style={{ marginBottom: '26px' }}>
                <p style={{ margin: '0 0 8px' }}>
                  <span style={{
                    fontSize: TYPE.subHeading, fontWeight: 700, color: COLORS.dark,
                    background: 'rgba(249,168,38,0.28)', padding: '2px 6px', borderRadius: '2px',
                  }}>{title}</span>
                </p>
                <p style={{ fontSize: TYPE.bodySm, lineHeight: 1.7, color: COLORS.text, margin: 0 }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability */}
      <section style={{ background: COLORS.creamAlt, padding: isWide ? '90px 28px' : '60px 24px' }}>
        <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
          <SectionHeading>Sustainability</SectionHeading>
          <div style={{ ...twoCol(false), alignItems: 'center' }}>
            <div>
              {SUSTAINABILITY.map(([title, paragraphs], i) => (
                <div key={title} style={{ marginTop: i === 0 ? 0 : '38px' }}>
                  <p style={{ fontSize: TYPE.subHeading, fontWeight: 700, color: COLORS.dark, margin: '0 0 12px' }}>{title}</p>
                  {paragraphs.map(p => (
                    <p key={p} style={{ ...body, margin: '0 0 12px' }}>{p}</p>
                  ))}
                </div>
              ))}
            </div>
            <Collage
              isWide={isWide}
              ratio="108%"
              items={[
                { src: IMG.aboutSustainMarket, alt: 'Summer vegetables at a farmers market', at: { top: 0, right: 0, width: '62%', height: '42%' } },
                { src: IMG.aboutSustainCrops, alt: 'Greens growing in a greenhouse', at: { top: '24%', left: 0, width: '56%', height: '48%' } },
                { src: IMG.aboutSustainBeets, alt: 'A handful of freshly picked beets', at: { bottom: 0, right: '4%', width: '52%', height: '44%' } },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Meet Claire */}
      <section style={{ background: COLORS.cream, padding: isWide ? '90px 28px' : '60px 24px' }}>
        <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
          <div style={{ ...twoCol(true), alignItems: 'start' }}>
            <figure style={{ margin: 0 }}>
              <img src={IMG.aboutClaire} alt="Claire, Square Fare founder" style={{
                width: '100%', height: isWide ? '520px' : '360px', objectFit: 'cover',
                borderRadius: '4px', display: 'block',
              }} />
              <figcaption style={{
                fontSize: TYPE.bodySm, lineHeight: 1.5, color: COLORS.muted, margin: '12px 0 0',
                textAlign: 'center', fontStyle: 'italic',
              }}>
                Claire at our semi-annual event for RDs and nutritionists
              </figcaption>
            </figure>
            <div>
              <h3 style={{ ...HEADING, fontSize: TYPE.bandHeading, margin: '0 0 20px' }}>
                Meet Claire, Square Fare Founder
              </h3>
              <p style={{ ...body, margin: '0 0 16px' }}>
                I’ve worked in corporate settings my entire career and anytime things got busy (always!) I’d find
                myself eating the same salad every day or getting a sandwich at 4pm. Sound familiar? I was trying to
                shed excess weight but felt fatigued and had stomach issues. During COVID, I made my own food and
                felt like a new person. I felt high energy, my stomach issues were gone, and I reached a weight that
                felt good.
              </p>
              <p style={{ ...body, margin: '0 0 16px' }}>
                I realized the importance of proper nutrition, and I understood that it can look different for
                different people.
              </p>
              <p style={{ ...body, margin: '0 0 16px' }}>
                That’s where the idea for Square Fare started: a tailored, individualized service that delivers the
                food you need and crave, while fitting into your busy life.
              </p>
              <p style={{ ...body, margin: 0 }}>
                Have questions or want to help? We would love to hear from you!{' '}
                <strong>
                  Reach out anytime:{' '}
                  <a href="mailto:claire@getsquarefare.com" style={{ color: COLORS.dark }}>claire@getsquarefare.com</a>.
                </strong>
              </p>
            </div>
          </div>
          <blockquote style={{
            maxWidth: '820px', margin: isWide ? '64px auto 0' : '44px auto 0', padding: 0, textAlign: 'center',
            ...HEADING, fontSize: TYPE.quote, lineHeight: 1.5, color: COLORS.dark,
          }}>
            “We are crafting a thoughtful package that’s adapted to you. When you sign up, you are buying the
            customization, analysis, and the care in cooking healthy food that go behind it.”
            <footer style={{
              fontFamily: FONTS.sans, fontSize: TYPE.bodySm, color: COLORS.muted, marginTop: '18px',
            }}>— Claire</footer>
          </blockquote>
        </div>
      </section>

      {/* Recent News */}
      <section style={{ background: COLORS.creamAlt, padding: isWide ? '70px 28px' : '52px 24px' }}>
        <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
          <SectionHeading>Recent News</SectionHeading>
          <p style={{ ...body, margin: '0 0 12px' }}>
            April 18 2025: Square Fare announces new <Link to="/glp1-meal-plan" style={{ color: COLORS.dark }}>GLP-1 Meal Plan</Link>{' '}
            in partnership with Amy Shapiro, MS, RD, CDN from{' '}
            <a href="https://realnutritionnyc.com/" target="_blank" rel="noopener noreferrer" style={{ color: COLORS.dark }}>Real Nutrition</a>
          </p>
          <p style={{ ...body, margin: 0 }}>
            April 1 2025:{' '}
            <a
              href="https://bellyblissnutrition.com/tips/2025/03/18/ibs-friendly-restaurants-nyc/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: COLORS.dark }}
            >
              Square Fare named best NYC meal delivery for people with IBS
            </a>
          </p>
        </div>
      </section>

      {/* Stay Updated — the shared band, over About's own photo. */}
      <NewsletterSignup
        source="about-page"
        onDark
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${IMG.aboutNewsletter})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          padding: isWide ? '96px 28px' : '64px 24px',
        }}
      />
    </StaticMarketingPage>
  );
}
